import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../admin/components/ConditionalAccess';
import { asAdminUserSession } from '../../../../admin/utils';
import { getDisplayName } from '../../../../models/NameUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateSetStatusMutation,
	useCancelAllInProgressAutomationsMutation,
	useClearPendingAutomationsMutation,
	useDuplicateAutomationTemplateMutation,
	useRestoreAutomationKeyFactsMutation,
} from '../../../../queries/Automation';
import { useDeleteAutomationTemplateMutation } from '../../../../queries/AutomationTemplate';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { baseStyleSheet } from '../../../styles/styles';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { LoadingSpinner } from '../../LoadingSpinner';
import { TextInputModal } from '../../TextInputModal';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { AutomationDisableCancelModal } from '../AutomationDisableCancelModal';
import { AutomationSteps } from '../AutomationSteps';
import { useAutomationCardData } from './hooks';
import {
	AutomationProgressStats,
	AutomationStatus,
	AutomationTrigger,
	AutomationYoloModeIndicator,
} from './presentation';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, RouteComponentProps<any> {
	automationTemplate: Api.IAutomationTemplateReference;
	canEdit?: boolean;
	isImpersonating?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	className?: string;
	hideActiveCount?: boolean;
	hideStatus?: boolean;
	maxStepsToDisplay?: number;
	onClick?(e: React.MouseEvent<HTMLElement>): void;
	/** Return true if consumer will handle editing */
	onRequestEdit?(): boolean;
	styles?: StyleDeclarationValue[];
}

export enum AutomationTemplateAction {
	ClearPendingAutomations = 'clearPendingAutomations',
	Copy = 'copy',
	Delete = 'delete',
	Disable = 'disable',
	Edit = 'edit',
	EditCanvas = 'editCanvas',
	Enable = 'enable',
	RestoreCancelledKeyFacts = 'restoreCancelledKeyFacts',
}

const CancelInProgressConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		title: 'Yes, Cancel All Automations',
	},
	{
		isCancelLink: true,
		title: 'No, I changed my mind',
	},
];

const _AutomationCard = ({
	automationTemplate,
	canEdit: canEditProp,
	isImpersonating: isImpersonatingProp,
	impersonationContext,
	className,
	logApiError,
	logEvent,
	logInput,
	hideActiveCount,
	hideStatus,
	history,
	match,
	maxStepsToDisplay,
	onRequestEdit,
	onClick,
	styles,
}: IProps) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const [showCancelInProgressModal, setShowCancelInProgressModal] = React.useState(false);
	const [showClearPendingConfirmation, setShowClearPendingConfirmation] = React.useState(false);
	const [showDuplicationNameInputModal, setShowDuplicationNameInputModal] = React.useState(false);
	const [showDisableConfirmation, setShowDisableConfirmation] = React.useState(false);
	const adminUserSession = getAdminUserSession();

	const updateStatusMutation = useAutomationTemplateSetStatusMutation({
		onSuccess: () => {
			// Success handled by individual callers
		},
		onError: error => {
			logApiError('UpdateAutomationTemplateStatus-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const deleteTemplateMutation = useDeleteAutomationTemplateMutation({
		onSuccess: () => {
			// Success handled silently
		},
		onError: (error: Api.IOperationResultNoValue) => {
			logApiError('DeleteAutomationTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const duplicateTemplateMutation = useDuplicateAutomationTemplateMutation({
		onSuccess: () => {
			// Success handled silently
		},
		onError: error => {
			logApiError('DuplicateAutomationTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const cancelAllInProgressMutation = useCancelAllInProgressAutomationsMutation({
		onSuccess: () => {
			// Success handled by caller
		},
		onError: error => {
			logApiError('CancelAllInProgress-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const clearPendingMutation = useClearPendingAutomationsMutation({
		onSuccess: () => {
			// Success handled by caller
		},
		onError: error => {
			logApiError('ClearPendingAutomations-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const restoreKeyFactsMutation = useRestoreAutomationKeyFactsMutation({
		onSuccess: () => {
			toast.success('Any future key facts have been restored.');
		},
		onError: error => {
			logApiError('RestoreCancelledKeyFactsForAutomationTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const isBusy =
		updateStatusMutation.isPending ||
		deleteTemplateMutation.isPending ||
		duplicateTemplateMutation.isPending ||
		cancelAllInProgressMutation.isPending ||
		clearPendingMutation.isPending ||
		restoreKeyFactsMutation.isPending;

	const canEdit = React.useMemo(() => {
		if (canEditProp !== undefined) {
			return canEditProp;
		}
		const isAdmin = userSession.user.role === Api.EUserRole.SuperAdmin || userSession.user.role === Api.EUserRole.Admin;
		return (
			automationTemplate.status !== Api.TemplateStatus.Archived &&
			(isAdmin || automationTemplate.scope === Api.TemplateScope.User)
		);
	}, [canEditProp, userSession.user.role, automationTemplate.status, automationTemplate.scope]);

	const isImpersonating = isImpersonatingProp ?? false;
	const {
		automationId,
		automationName,
		completedContactString,
		inProgressContactString,
		statusIcon,
		trigger,
		triggerDescription,
		showCompletedButton,
		showInProgressButton,
	} = useAutomationCardData(automationTemplate, isImpersonating);

	const navigateToEditor = () => {
		logInput('EditAutomation', 'Click', { id: automationTemplate.id });
		const handledExternally = onRequestEdit?.();
		if (!handledExternally) {
			history?.push({
				pathname: `${match.url}/${automationTemplate.id}`,
			});
		}
	};

	const hasPublishedVersion = !!automationTemplate.publishedVersionId;

	const navigateToCanvasEditor = () => {
		logInput('EditAutomationCanvas', 'Click', { id: automationTemplate.id });

		const accountId = (match.params as { accountId?: string } | undefined)?.accountId;
		const pathname = accountId
			? `/accounts/${accountId}/automations/${automationTemplate.id}/canvas`
			: `${match.url}/${automationTemplate.id}/canvas`;

		history?.push({ pathname });
	};

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		if (onClick) {
			onClick(e);
		}
		if (!e.defaultPrevented) {
			// Keep the primary card click behavior consistent with the admin dropdown menu.
			// Admins can manage draft-only automations in the canvas editor as well.
			if (adminUserSession && canEdit && automationTemplate.status !== Api.TemplateStatus.Archived) {
				navigateToCanvasEditor();
			} else {
				navigateToEditor();
			}
		}
	};

	const onMenuItemClicked = (menuItem: AutomationTemplateAction) => {
		switch (menuItem) {
			case AutomationTemplateAction.Copy: {
				logInput('DuplicateAutomationTemplate', 'Click', {
					id: automationTemplate.id,
				});
				setShowDuplicationNameInputModal(true);
				break;
			}
			case AutomationTemplateAction.Delete: {
				logInput('DeleteAutomationTemplate', 'Click', {
					id: automationTemplate.id,
				});
				deleteTemplateMutation.mutate({ id: automationTemplate.id });
				break;
			}
			case AutomationTemplateAction.Enable: {
				updateStatusMutation.mutate({
					id: automationTemplate.id,
					status: Api.TemplateStatus.Published,
					impersonationContext,
				});
				break;
			}
			case AutomationTemplateAction.Disable: {
				if (automationTemplate?.stats?.inProgress >= 0) {
					setShowDisableConfirmation(true);
					return;
				}
				break;
			}
			case AutomationTemplateAction.Edit: {
				navigateToEditor();
				break;
			}
			case AutomationTemplateAction.EditCanvas: {
				navigateToCanvasEditor();
				break;
			}
			case AutomationTemplateAction.RestoreCancelledKeyFacts: {
				logInput('RestoreCancelledKeyFactsForAutomationTemplate', 'Click', {
					id: automationTemplate.id,
				});
				restoreKeyFactsMutation.mutate({ id: automationTemplate.id, impersonationContext });
				break;
			}
			case AutomationTemplateAction.ClearPendingAutomations: {
				setShowClearPendingConfirmation(true);
				break;
			}
			default: {
				break;
			}
		}
	};
	const onPublishChangesClicked = () => {
		logInput('PublishChanges', 'Click', { id: automationTemplate.id });
		navigateToEditor();
	};
	const onCancelAutomationsClick = () => {
		setShowCancelInProgressModal(true);
	};
	const cancelAutomationInProgress = async () => {
		const numAutomations = automationTemplate.stats?.inProgress;

		cancelAllInProgressMutation.mutate(
			{ id: automationTemplate.id, impersonationContext },
			{
				onSuccess: () => {
					toast.success(`${numAutomations} automation${numAutomations > 1 ? 's' : ''} cancelled`);
				},
			}
		);
	};
	const onCancelInProgressRequestClose = async (_?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		if (!cancel) {
			await cancelAutomationInProgress();
		}
		setShowCancelInProgressModal(false);
	};

	const duplicateAutomation = async (duplicateName: string) => {
		logEvent('DuplicateAutomation', {
			id: automationTemplate.id,
			nameLength: duplicateName.length,
		});
		duplicateTemplateMutation.mutate({
			id: automationTemplate.id,
			name: duplicateName,
			impersonationContext,
		});
	};

	const onDuplicateAutomationNameInputModalRequestClose = async (result?: string, cancel?: boolean) => {
		if (!cancel && !!result) {
			await duplicateAutomation(result);
		}
		setShowDuplicationNameInputModal(false);
	};

	const onDisableModalClosed = () => {
		setShowDisableConfirmation(false);
	};

	const clearPendingAutomations = async () => {
		clearPendingMutation.mutate(
			{ id: automationTemplate.id, impersonationContext },
			{
				onSuccess: () => {
					toast.success('Pending automations cleared successfully.');
				},
			}
		);
	};

	const onClearPendingAutomationsRequestClose = async (_?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		if (!cancel) {
			await clearPendingAutomations();
		}
		setShowClearPendingConfirmation(false);
	};

	function getAdminUserSession() {
		return asAdminUserSession(userSession);
	}

	const hasDraftVersion = !!automationTemplate.draftTrigger || (automationTemplate.draftSteps?.length ?? 0) > 0;
	const publishedTrigger = automationTemplate.publishedTrigger;

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} automation-card ${className || ''}`}>
			<button className={css(baseStyleSheet.truncateText, styleSheet.name)} onClick={handleClick}>
				<span>{automationTemplate.name}</span>
			</button>
			<Dropdown>
				<DropdownEllipsisTrigger disabled={isBusy} styles={[styleSheet.moreMenu]} />
				<DropdownContent styles={[styleSheet.moreMenuContent]}>
					{automationTemplate?.scope === Api.TemplateScope.Industry ? (
						<>
							<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Edit)}>View</DropdownItem>
							{userSession.featurePermissions?.automation?.write && (
								<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Copy)}>Duplicate</DropdownItem>
							)}
						</>
					) : null}
					{adminUserSession ? (
						<>
							{canEdit && automationTemplate.status !== Api.TemplateStatus.Archived && (
								<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.EditCanvas)}>
									Manage
								</DropdownItem>
							)}
							<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Edit)}>
								{automationTemplate.status === Api.TemplateStatus.Archived || !canEdit
									? 'View Legacy'
									: 'Manage Legacy'}
							</DropdownItem>
						</>
					) : (
						<>
							<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Edit)}>
								{automationTemplate.status === Api.TemplateStatus.Archived || !canEdit ? 'View' : 'Manage'}
							</DropdownItem>
							{canEdit && hasPublishedVersion && automationTemplate.status !== Api.TemplateStatus.Archived && (
								<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.EditCanvas)}>
									Manage Canvas
								</DropdownItem>
							)}
						</>
					)}
					{canEdit ? (
						<>
							<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Copy)}>Duplicate</DropdownItem>
							{automationTemplate.status === Api.TemplateStatus.Published ? (
								<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Disable)}>Disable</DropdownItem>
							) : null}
							{automationTemplate.status === Api.TemplateStatus.Disabled ? (
								<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Enable)}>Enable</DropdownItem>
							) : null}
							{(automationTemplate.status !== Api.TemplateStatus.Archived &&
								automationTemplate.status !== Api.TemplateStatus.Published) ||
							(automationTemplate.status === Api.TemplateStatus.Published &&
								automationTemplate.stats?.inProgress === 0) ? (
								<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.Delete)}>
									{hasPublishedVersion ? 'Archive' : 'Delete'}
								</DropdownItem>
							) : null}
						</>
					) : null}
					{adminUserSession?.hasPermission(AdminPermission.CancelAutomations) &&
					!!hasPublishedVersion &&
					publishedTrigger?._type === Api.AutomationTriggerType.ResourceSelector ? (
						<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.RestoreCancelledKeyFacts)}>
							Restore Cancelled Key Facts
						</DropdownItem>
					) : null}
					{adminUserSession?.hasPermission(AdminPermission.CancelAutomations) ? (
						<DropdownItem onClick={() => onMenuItemClicked(AutomationTemplateAction.ClearPendingAutomations)}>
							Clear Pending
						</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
			{!hideStatus && automationTemplate?.scope !== Api.TemplateScope.Industry && (
				<div className={css(styleSheet.splitSection)}>
					<AutomationStatus name={automationName} icon={statusIcon} />

					<div className={css(styleSheet.sectionInner)}>
						<div className={css(styleSheet.sectionLabel)}>Creator</div>
						<div
							className={css(
								baseStyleSheet.horizontalStack,
								baseStyleSheet.truncateText,
								styleSheet.statusSectionContent
							)}
						>
							{getDisplayName(automationTemplate?.creator, true)}
						</div>
					</div>
				</div>
			)}

			<AutomationTrigger trigger={trigger} triggerDescription={triggerDescription} />

			<div className={css(styleSheet.section)}>
				<div className={css(styleSheet.sectionLabel)}>
					{`${automationTemplate.publishedSteps?.length || 0} Step${
						(automationTemplate.publishedSteps?.length || 0) > 1 ? 's' : ''
					} - ${automationTemplate.runtimeDays || 0}-Day Runtime`}
				</div>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<AutomationSteps
						publishedStepReferences={automationTemplate.publishedSteps}
						draftStepReferences={automationTemplate.draftSteps}
						status={automationTemplate.status}
						maxStepsToDisplay={maxStepsToDisplay}
					/>
				</div>
			</div>
			{!hideActiveCount && automationTemplate?.scope !== Api.TemplateScope.Industry && (
				<AutomationProgressStats
					automationId={automationId}
					showCompletedButton={showCompletedButton}
					showInProgressButton={showInProgressButton}
					completedContactString={completedContactString}
					inProgressContactString={inProgressContactString}
				/>
			)}
			<footer className={css(styleSheet.section, styleSheet.publishButtonContainer)}>
				{!!hasDraftVersion && !!canEdit && automationTemplate?.scope !== Api.TemplateScope.Industry && (
					<button
						className={css(baseStyleSheet.ctaButtonSmall, styleSheet.publishButton)}
						onClick={onPublishChangesClicked}
					>
						<span>Publish Changes</span>
					</button>
				)}
			</footer>
			{(automationTemplate.stats?.inProgress ?? 0) > 0 && (
				<ConditionalAccess
					permission={AdminPermission.OnlyForDevelopers}
					or={[AdminPermission.SupportLevel3, AdminPermission.CancelAutomations]}
				>
					<div className={css(styleSheet.section)}>
						<button
							className={css(baseStyleSheet.ctaButtonDestructiveSmall, styleSheet.cancelAutomationButton)}
							disabled={cancelAllInProgressMutation.isPending}
							onClick={onCancelAutomationsClick}
						>
							<span>
								{cancelAllInProgressMutation.isPending ? (
									<LoadingSpinner type='tiny' />
								) : (
									`Cancel Automation${(automationTemplate.stats?.inProgress ?? 0) > 1 ? 's' : ''}`
								)}
							</span>
						</button>
						{showCancelInProgressModal && (
							<ConfirmationDialog
								icon={<WarningIcon />}
								modalProps={{
									isOpen: showCancelInProgressModal,
									onRequestClose: onCancelInProgressRequestClose,
								}}
								options={CancelInProgressConfirmationOptions}
								stackButtonsVertically={true}
								title={<span>Are you sure?</span>}
							>
								<div style={{ textAlign: 'center' }}>
									{`This will cancel ${
										automationTemplate.stats?.inProgress > 1
											? `all ${automationTemplate.stats.inProgress} automations`
											: 'the automation'
									} in process`}
									<br />
									and once you&apos;ve cancelled, it cannot be undone.
								</div>
							</ConfirmationDialog>
						)}
					</div>
				</ConditionalAccess>
			)}
			<AutomationYoloModeIndicator trigger={trigger} />
			{!!showDuplicationNameInputModal && (
				<TextInputModal
					cta='Duplicate'
					modalProps={{
						isOpen: !!showDuplicationNameInputModal,
						onRequestClose: onDuplicateAutomationNameInputModalRequestClose,
					}}
					placeholderText='New Automation name'
					title={`Duplicate Automation: "${automationTemplate?.name}"`}
				/>
			)}
			<AutomationDisableCancelModal
				automationTemplate={automationTemplate}
				isOpen={showDisableConfirmation}
				onRequestClose={onDisableModalClosed}
				impersonationContext={impersonationContext}
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showClearPendingConfirmation,
					onRequestClose: onClearPendingAutomationsRequestClose,
				}}
				options={[
					{ isDestructive: true, representedObject: true, title: 'Confirm' },
					{ isCancel: true, representedObject: false, title: 'Cancel' },
				]}
				title={
					<span>
						This will clear any suggested usages of this automation from contacts.
						<br />
						<span className={css(baseStyleSheet.fontBold)}>These suggestions cannot be restored.</span>
					</span>
				}
			/>
			{isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
		</div>
	);
};

const AutomationCardWithRouter = withRouter(_AutomationCard);
export const AutomationCard = withEventLogging(AutomationCardWithRouter, 'AutomationCard');
